/**
 * MyTuiProfileCustomLogger - Unified logging service for MyTuiProfile web application
 *
 * Environment Behavior:
 * - Jest Test (NODE_ENV === 'test'): All logs suppressed
 * - Development/Test/Pre: All log levels output to console
 * - Production: Only ERROR logs output to console
 * - Datadog RUM: Captures errors in ALL environments
 *
 * @param {string} prefix - Configurable prefix for log messages (e.g., "F2W", "TPP", "MTP")
 */
class MyTuiProfileCustomLogger {
  /**
   * Creates a new MyTuiProfileCustomLogger instance
   * @param {string} prefix - The prefix to use in log messages (default: 'MTP')
   */
  constructor(prefix = 'MTP') {
    this.prefix = prefix;

    // Environment detection using NODE_ENV and REACT_APP_ENVIRONMENT
    // Requirement 1.9: Detect environment using combination of variables
    this.isJestTest = process.env.NODE_ENV === 'test';
    this.isProduction = process.env.REACT_APP_ENVIRONMENT === 'prod' || process.env.NODE_ENV === 'production';
    this.isDevelopment = process.env.NODE_ENV === 'development';

    // Additional environment flags for test/pre environments
    this.isTestPre = process.env.REACT_APP_ENVIRONMENT === 'test' || process.env.REACT_APP_ENVIRONMENT === 'pre';

    // Deduplication: prevent sending the same error to Datadog multiple times in a short window
    this._recentErrors = new Map(); // fingerprint -> timestamp
    this._dedupeWindowMs = 5000; // 5 second deduplication window
  }

  /**
   * Check if an error should be reported to Datadog (deduplication).
   * Prevents sending the same error fingerprint multiple times within the dedup window.
   * @param {string} fingerprint - Unique identifier for the error
   * @returns {boolean} True if the error should be reported
   */
  _shouldReportToDatadog(fingerprint) {
    const now = Date.now();
    const lastSeen = this._recentErrors.get(fingerprint);
    if (lastSeen && now - lastSeen < this._dedupeWindowMs) {
      return false; // Skip duplicate within window
    }
    this._recentErrors.set(fingerprint, now);
    // Cleanup old entries to prevent memory leak
    if (this._recentErrors.size > 50) {
      for (const [key, ts] of this._recentErrors) {
        if (now - ts > this._dedupeWindowMs) this._recentErrors.delete(key);
      }
    }
    return true;
  }

  /**
   * Log informational messages (dev/test/pre only)
   * @param {string} message - Log message
   * @param {Object} context - Additional context
   */
  log(message, context = {}) {
    if (this.isJestTest) return;
    // In production, suppress non-error logs (Requirement 1.6)
    if (this.isProduction) return;
    // In dev/test/pre environments, output to console (Requirement 1.5)
    console.log('[%s LOG]', this.prefix, message, context);
  }

  /**
   * Log errors (all environments except test, sent to Datadog in ALL environments)
   * @param {string} message - Error message
   * @param {Error} error - Error object
   * @param {Object} context - Additional context
   */
  error(message, error = null, context = {}) {
    if (this.isJestTest) return;

    const errorContext = {
      timestamp: new Date().toISOString(),
      ...context,
    };

    // Console output in dev/test/pre environments with full stack trace (Requirement 1.3)
    // In production, still log errors to console (Requirement 1.6 - only ERROR logs in prod)
    console.error('[%s ERROR]', this.prefix, message, {
      error: error?.message || error,
      stack: error?.stack,
      ...errorContext,
    });

    // Datadog RUM - send in ALL environments (Requirement 1.7)
    // Deduplicate: only send if not recently reported with same fingerprint
    if (window.DD_RUM) {
      const fingerprint = `${message}:${error?.message || String(error || '')}`;
      if (this._shouldReportToDatadog(fingerprint)) {
        try {
          window.DD_RUM.addError(error || new Error(String(message)), errorContext);
        } catch (ddError) {
          // Fail silently - don't break the app if Datadog fails
          console.warn('[%s] Failed to send error to Datadog', this.prefix, ddError);
        }
      }
    }
  }

  /**
   * Log warnings (dev/test/pre only)
   * @param {string} message - Warning message
   * @param {Object} context - Additional context
   */
  warn(message, context = {}) {
    if (this.isJestTest) return;
    // In production, suppress non-error logs (Requirement 1.6)
    if (this.isProduction) return;
    // In dev/test/pre environments, output to console (Requirement 1.5)
    console.warn('[%s WARN]', this.prefix, message, context);
  }

  /**
   * Log info messages (dev/test/pre only)
   * @param {string} message - Info message
   * @param {Object} context - Additional context
   */
  info(message, context = {}) {
    if (this.isJestTest) return;
    // In production, suppress non-error logs (Requirement 1.6)
    if (this.isProduction) return;
    // In dev/test/pre environments, output to console (Requirement 1.5)
    console.info('[%s INFO]', this.prefix, message, context);
  }

  /**
   * Log debug messages (dev/test/pre only)
   * @param {string} message - Debug message
   * @param {Object} context - Additional context
   */
  debug(message, context = {}) {
    if (this.isJestTest) return;
    // In production, suppress non-error logs (Requirement 1.6)
    if (this.isProduction) return;
    // In dev/test/pre environments, output to console (Requirement 1.5)
    console.debug('[%s DEBUG]', this.prefix, message, context);
  }

  /**
   * Log API errors with structured format
   * @param {string} endpoint - API endpoint
   * @param {Error} error - Error object
   * @param {Object} context - Additional context
   */
  apiError(endpoint, error, context = {}) {
    if (this.isJestTest) return;

    // Sanitize endpoint to prevent format string injection
    const sanitizedEndpoint = String(endpoint).replace(/%/g, '%%');

    // Build structured context with endpoint, status code, and timestamp (Requirement 1.8)
    const apiErrorContext = {
      endpoint: sanitizedEndpoint,
      status: error?.response?.status || null,
      timestamp: new Date().toISOString(),
      source: 'api',
      ...context,
    };

    // Console output with full context (Requirement 1.8)
    // In production, still log API errors to console (Requirement 1.6 - only ERROR logs in prod)
    console.error('[%s API_ERROR] %s', this.prefix, sanitizedEndpoint, {
      error: error?.message || error,
      stack: error?.stack,
      ...apiErrorContext,
    });

    // Datadog RUM - send in ALL environments (Requirement 1.7)
    // Deduplicate: only send if not recently reported with same fingerprint
    if (window.DD_RUM) {
      const fingerprint = `api:${sanitizedEndpoint}:${error?.message || ''}`;
      if (this._shouldReportToDatadog(fingerprint)) {
        try {
          const apiError = error || new Error('API Error: '.concat(sanitizedEndpoint));
          window.DD_RUM.addError(apiError, apiErrorContext);
        } catch (ddError) {
          // Fail silently - don't break the app if Datadog fails
          console.warn('[%s] Failed to send API error to Datadog', this.prefix, ddError);
        }
      }
    }
  }

  /**
   * Log component lifecycle events (only in dev/test/pre)
   * App-specific method for MyTuiProfile
   * @param {string} componentName - Component name
   * @param {string} event - Event description
   * @param {Object} data - Additional data
   */
  component(componentName, event, data = {}) {
    if (this.isJestTest) return;
    // In production, suppress non-error logs (Requirement 1.6)
    if (this.isProduction) return;
    // In dev/test/pre environments, output to console (Requirement 1.5)
    console.log('[%s %s] %s', this.prefix, componentName, event, data);
  }
}

// Export the class for creating custom instances with different prefixes
export { MyTuiProfileCustomLogger };

// Export a default singleton instance with 'MTP' prefix for backward compatibility
export default new MyTuiProfileCustomLogger('MTP');
